import { FC, useState } from 'react';
import type { ActiveRemediationRunSummary } from '@libs/types';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  CaretDownIcon,
  CaretRightIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react';

import { useRemediationRun } from '../api/remediation-api';
import { totalsAcrossRuns } from '../utils/run-counts';

import { RemediationDialog } from './RemediationDialog';
import { ItemStatusBadge, RunStatusBadge } from './RemediationStatusBadge';
import { RunOutcomeBreakdown } from './RunOutcomeBreakdown';

/**
 * The documents of one run.
 *
 * Its own component so the fetch is scoped to the run the user actually opened.
 * A history dialog that loaded every run's documents up front would, for an
 * assessment remediated three times, pull three runs' worth of items to show a
 * list the user may never expand — and `GET /remediation/runs` deliberately
 * omits items for exactly that reason.
 */
const RunDocuments: FC<{ runId: string }> = observer(({ runId }) => {
  const { t } = useTrans();
  const theme = useTheme();
  const { data, isLoading, isError } = useRemediationRun(runId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Typography variant="textXS_regular" sx={{ color: 'error.main', py: 1, display: 'block' }}>
        {t('app.remediation.history.loadRunError')}
      </Typography>
    );
  }

  if (data.items.length === 0) {
    return (
      <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', py: 1, display: 'block' }}>
        {t('app.remediation.history.noDocuments')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
      {data.items.map((item) => (
        <Box
          key={item.itemId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.625,
            px: 1,
            borderRadius: 1,
            backgroundColor: theme.palette.grey[100],
            minWidth: 0,
          }}
        >
          <Typography
            variant="textSM_regular"
            sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
          >
            {item.documentName ?? t('app.remediation.history.untitledDocument')}
          </Typography>
          {item.domainKey && (
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
              {item.domainKey}
            </Typography>
          )}
          <Box sx={{ flexShrink: 0 }}>
            <ItemStatusBadge status={item.status} />
          </Box>
        </Box>
      ))}
    </Box>
  );
});

/** One run in the history list, collapsed until asked for. */
const RunRow: FC<{ run: ActiveRemediationRunSummary; index: number; language: string }> = observer(
  ({ run, index, language }) => {
    const theme = useTheme();
    const { t } = useTrans();
    const [open, setOpen] = useState(false);

    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Box
          component="button"
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            textAlign: 'start',
            fontFamily: 'inherit',
          }}
        >
          {open ? <CaretDownIcon size={14} /> : <CaretRightIcon size={14} />}
          <Typography variant="textSM_semiBold">
            {t('app.remediation.history.runLabel', { number: index })}
          </Typography>
          <RunStatusBadge status={run.status} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
            {new Date(run.createdAt).toLocaleDateString(language)}
          </Typography>
        </Box>

        <RunOutcomeBreakdown counts={run.itemCounts} />

        <Collapse in={open} unmountOnExit>
          <RunDocuments runId={run.runId} />
        </Collapse>
      </Box>
    );
  },
);

/**
 * Every remediation an assessment has been through.
 *
 * The table can only show the newest run's state, so an assessment remediated
 * over several passes had no surface reporting what those passes did — how many
 * there were, how much they improved, or which documents each one touched. That
 * is the record a compliance reviewer is asked for after the fact, and it was
 * only reconstructable by opening each run in turn.
 *
 * Runs are listed oldest-first here, against the newest-first order everywhere
 * else: this is a history, and a history reads forwards.
 */
export const RemediationHistoryDialog: FC<{
  open: boolean;
  onClose: () => void;
  assessmentTitle: string;
  /** Every run on this assessment's lineage, in the API's newest-first order. */
  runs: ActiveRemediationRunSummary[];
  language: string;
  onOpenRun: (runId: string) => void;
}> = observer(({ open, onClose, assessmentTitle, runs, language, onOpenRun }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const totals = totalsAcrossRuns(runs);
  const ordered = [...runs].reverse();
  const latest = runs[0];

  return (
    <RemediationDialog
      open={open}
      onClose={onClose}
      title={t('app.remediation.history.title')}
      subtitle={assessmentTitle}
      icon={<ClockCounterClockwiseIcon size={20} />}
      // A run history is a list of passes with expandable document lists — it
      // needs the room a confirmation does not.
      maxWidth="md"
    >

        {/* The three numbers the question "what has remediation done for this
            assessment" actually asks for, summed across every pass. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 2,
            mb: 2,
          }}
        >
          {(
            [
              ['runs', totals.runs],
              ['applied', totals.applied],
              ['documents', totals.documents],
            ] as const
          ).map(([key, value], i) => (
            <Box
              key={key}
              sx={{
                p: 1.5,
                borderInlineStart: i === 0 ? 'none' : `1px solid ${theme.palette.grey[200]}`,
                minWidth: 0,
              }}
            >
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary', display: 'block' }}>
                {t(`app.remediation.history.stat.${key}` as SingularKeys)}
              </Typography>
              <Typography variant="textLG_semiBold" sx={{ display: 'block' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {runs.length === 0 ? (
          <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
            {t('app.remediation.history.empty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ordered.map((run, i) => (
              <RunRow key={run.runId} run={run} index={i + 1} language={language} />
            ))}
          </Box>
        )}

        {latest && (
          <Button
            variant="secondary"
            onClick={() => onOpenRun(latest.runId)}
            sx={{ mt: 2, width: '100%' }}
          >
            {t('app.remediation.history.openLatest')}
          </Button>
        )}
    </RemediationDialog>
  );
});
